import { useCallback, useRef } from 'react';

export type HapticIntensity = 'light' | 'medium' | 'strong';

export const hapticVariants = {
  light: {
    x: [0, -1, 1, -1, 0],
    transition: { duration: 0.05, ease: "easeInOut" }
  },
  medium: {
    x: [0, -2, 2, -2, 1, -1, 0],
    transition: { duration: 0.08, ease: "easeInOut" }
  },
  strong: {
    x: [0, -3, 3, -3, 2, -2, 1, -1, 0],
    scale: [1, 1.02, 0.98, 1],
    transition: { duration: 0.12, ease: "easeInOut" }
  }
};

export const useHapticFeedback = () => {
  const lastValue = useRef<number | null>(null);
  
  const getHapticIntensity = useCallback((
    value: number, 
    min: number, 
    max: number,
    keyValues?: number[]
  ): HapticIntensity => {
    // Strong haptic at min/max boundaries
    if (value === min || value === max) return 'strong';
    
    // Strong haptic at key milestone values
    if (keyValues?.includes(value)) return 'strong';
    
    // Medium haptic at round numbers (multiples of 100, 50, etc.)
    if (value % 100 === 0 || value % 50 === 0) return 'medium';
    
    // Light haptic for regular changes
    return 'light';
  }, []);
  
  const shouldTriggerHaptic = useCallback((newValue: number): boolean => {
    if (lastValue.current === null || lastValue.current !== newValue) {
      lastValue.current = newValue;
      return true;
    }
    return false;
  }, []);
  
  // Try to use native haptic API if available
  const triggerNativeHaptic = useCallback((intensity: HapticIntensity) => {
    if ('vibrate' in navigator) {
      const patterns: Record<HapticIntensity, number[]> = {
        light: [10],
        medium: [15, 10, 15],
        strong: [20, 15, 20, 15, 20]
      };
      navigator.vibrate(patterns[intensity]);
    }
  }, []);
  
  return { 
    hapticVariants, 
    getHapticIntensity, 
    shouldTriggerHaptic,
    triggerNativeHaptic 
  };
};

export default useHapticFeedback;
